/**
 * One shared brake for every automatic page reload.
 *
 * Two independent mechanisms reload this app on their own: a lazy chunk that 404s after a deploy
 * (`chunkReload.ts`) and a service worker that has a newer version waiting (`swUpdate.ts`). They
 * fire in exactly the same situation — a deploy landed while the tab was open — so each would see
 * the other's reload as a fresh reason to reload again. Sharing one timestamp makes that
 * impossible: whichever gets there first wins, and the other stands down.
 *
 * The guard is a timestamp rather than a flag, deliberately. A flag has to be cleared once the
 * page loads successfully, and that clearing is precisely what would arm an endless loop when the
 * fresh page is broken too. A timestamp answers the only question that matters — «did we already
 * try this, just now?» — and needs no cleanup.
 */
const RELOAD_AT_KEY = 'tt:reload-at';
const COOLDOWN_MS = 15_000;

function readLastReload(): number | null {
  const raw = window.sessionStorage.getItem(RELOAD_AT_KEY);
  if (raw === null) return null;
  const at = Number.parseInt(raw, 10);
  return Number.isFinite(at) ? at : null;
}

/**
 * True when a reload happened within the cooldown — or when we cannot tell.
 *
 * Private browsing and storage-blocking extensions make sessionStorage throw. Without a way to
 * remember, the safe answer is «yes, recently»: refusing to reload shows an error the person can
 * act on, whereas reloading blindly could loop forever on a page that never recovers.
 */
export function recentlyReloaded(): boolean {
  try {
    const at = readLastReload();
    return at !== null && Date.now() - at < COOLDOWN_MS;
  } catch {
    return true;
  }
}

/**
 * Reload the page unless one just happened. Returns whether it actually reloaded, so callers can
 * fall back to showing something instead of assuming the page is about to disappear.
 */
export function reloadOnce(): boolean {
  if (recentlyReloaded()) return false;
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Ignored — `recentlyReloaded` already fails closed when storage is unavailable, so we can
    // never get here twice in a row anyway.
  }
  window.location.reload();
  return true;
}
